import { z } from 'zod';
const uuidSchema = z.string().uuid();
const dateSchema = z.string().date();
const dateTimeSchema = z.string().datetime({ offset: true });
const revisionSchema = z.number().int().positive();
export const canteenOperationsContractVersionSchema = z.literal('1');
export const integrationSourceSchema = z.enum([
    'canteen',
    'iam',
    'cards',
    'billing',
    'dms',
    'workflow',
    'messaging',
    'notifications',
]);
export const projectionFreshnessSchema = z.enum([
    'current',
    'stale',
    'unavailable',
]);
export const integrationProjectionSchema = z
    .object({
    contractVersion: canteenOperationsContractVersionSchema,
    institutionId: uuidSchema,
    source: integrationSourceSchema,
    sourceContractVersion: z.string().min(1).max(40),
    resourceType: z.string().min(1).max(100),
    resourceId: z.string().min(1).max(200),
    observedAt: dateTimeSchema,
    freshness: projectionFreshnessSchema,
    reconciliationState: z.enum([
        'not_required',
        'pending',
        'reconciled',
        'conflict',
    ]),
    unavailableReason: z.string().min(1).max(500).nullable(),
    data: z.unknown().nullable(),
})
    .superRefine((projection, context) => {
    if (projection.freshness === 'unavailable' &&
        projection.unavailableReason === null)
        context.addIssue({
            code: 'custom',
            message: 'Niedostępne źródło musi podać przyczynę.',
            path: ['unavailableReason'],
        });
    if (projection.freshness === 'unavailable' && projection.data !== null)
        context.addIssue({
            code: 'custom',
            message: 'Brak źródła nie może być przedstawiony jako dane zerowe.',
            path: ['data'],
        });
});
export const canteenDelegationPermissionSchema = z.enum([
    'plan',
    'reserve',
    'cancel',
    'view_finance',
    'manage_finance',
    'issue',
    'manage_diet_profile',
]);
export const canteenDelegationAudienceSchema = z
    .object({
    personIds: z.array(uuidSchema).default([]),
    groupIds: z.array(uuidSchema).default([]),
    includeNestedGroups: z.boolean().default(true),
})
    .refine((audience) => audience.personIds.length > 0 || audience.groupIds.length > 0, { message: 'Delegacja musi wskazywać osobę lub grupę.' });
export const createCanteenDelegationSchema = z
    .object({
    contractVersion: canteenOperationsContractVersionSchema,
    institutionId: uuidSchema,
    canteenId: uuidSchema,
    principalId: uuidSchema,
    audience: canteenDelegationAudienceSchema,
    permissions: z.array(canteenDelegationPermissionSchema).min(1),
    pointIds: z.array(uuidSchema).default([]),
    itemTypeIds: z.array(uuidSchema).default([]),
    validFrom: dateTimeSchema,
    validUntil: dateTimeSchema.nullable(),
    reason: z.string().trim().min(3).max(500),
    requestedBy: uuidSchema,
    idempotencyKey: z.string().min(16).max(200),
})
    .superRefine((delegation, context) => {
    if (delegation.validUntil !== null &&
        new Date(delegation.validUntil) <= new Date(delegation.validFrom))
        context.addIssue({
            code: 'custom',
            message: 'Koniec delegacji musi być późniejszy od początku.',
            path: ['validUntil'],
        });
    if (delegation.permissions.includes('manage_finance') &&
        !delegation.permissions.includes('view_finance'))
        context.addIssue({
            code: 'custom',
            message: 'Zarządzanie finansami wymaga prawa do ich odczytu.',
            path: ['permissions'],
        });
});
export const canteenConfigurationStateSchema = z.enum([
    'draft',
    'scheduled',
    'active',
    'retired',
]);
export const canteenItemTypeVersionSchema = z
    .object({
    id: uuidSchema,
    itemTypeId: uuidSchema,
    institutionId: uuidSchema,
    canteenId: uuidSchema,
    code: z.string().regex(/^[a-z][a-z0-9_]{1,49}$/),
    name: z.string().trim().min(1).max(120),
    category: z.enum([
        'meal',
        'service',
        'disposal',
        'operational_cost',
        'custom',
    ]),
    sequence: z.number().int().min(0).max(10_000),
    reservable: z.boolean(),
    issuable: z.boolean(),
    billable: z.boolean(),
    quantityMode: z.enum(['single', 'multiple', 'decimal']),
    state: canteenConfigurationStateSchema,
    effectiveFrom: dateTimeSchema,
    effectiveUntil: dateTimeSchema.nullable(),
    revision: revisionSchema,
})
    .refine((version) => version.effectiveUntil === null ||
    version.effectiveUntil > version.effectiveFrom, {
    message: 'Koniec wersji musi być późniejszy od początku.',
    path: ['effectiveUntil'],
});
export const menuPublicationPreviewSchema = z.object({
    contractVersion: canteenOperationsContractVersionSchema,
    previewId: uuidSchema,
    previewHash: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    institutionId: uuidSchema,
    canteenId: uuidSchema,
    startsOn: dateSchema,
    endsOn: dateSchema,
    audienceCount: z.number().int().nonnegative(),
    changedItemCount: z.number().int().nonnegative(),
    reservationsToReevaluate: z.number().int().nonnegative(),
    blockingIssues: z.array(z.object({
        code: z.string().min(1).max(100),
        message: z.string().min(1).max(500),
        resourceId: z.string().min(1).max(200),
    })),
    createdAt: dateTimeSchema,
    expiresAt: dateTimeSchema,
});
export const canteenReportQuerySchema = z
    .object({
    contractVersion: canteenOperationsContractVersionSchema,
    institutionId: uuidSchema,
    canteenId: uuidSchema,
    startsOn: dateSchema,
    endsOn: dateSchema,
    groupIds: z.array(uuidSchema).default([]),
    itemTypeIds: z.array(uuidSchema).default([]),
    states: z.array(z.string().min(1).max(60)).default([]),
    dimensions: z
        .array(z.enum([
        'day',
        'group',
        'person',
        'item_type',
        'offer_variant',
        'state',
        'point',
    ]))
        .min(1),
    measures: z
        .array(z.enum([
        'reserved_quantity',
        'issued_quantity',
        'exception_quantity',
        'financial_effect_minor',
    ]))
        .min(1),
    cursor: z.string().min(1).nullable().default(null),
    pageSize: z.number().int().min(1).max(500).default(100),
})
    .refine((query) => query.startsOn <= query.endsOn, {
    message: 'Początek raportu nie może być późniejszy niż koniec.',
    path: ['endsOn'],
});
export const canteenDocumentRequestSchema = z.object({
    contractVersion: canteenOperationsContractVersionSchema,
    institutionId: uuidSchema,
    canteenId: uuidSchema,
    documentType: z.enum([
        'payment_receipt',
        'reservation_history',
        'issuance_report',
        'requisition',
        'stock_document',
    ]),
    templateVersionId: uuidSchema,
    factSnapshotId: uuidSchema,
    requestedBy: uuidSchema,
    idempotencyKey: z.string().min(16).max(200),
    correlationId: uuidSchema,
});
export const canteenWorkflowReferenceSchema = z.object({
    sourceAggregateType: z.enum([
        'diet_profile_change',
        'issuance_exception',
        'requisition',
        'invoice_difference',
        'refund',
    ]),
    sourceAggregateId: uuidSchema,
    sourceAggregateVersion: revisionSchema,
    workflowCaseId: uuidSchema,
    state: z.enum(['open', 'waiting', 'approved', 'rejected', 'cancelled']),
    dueAt: dateTimeSchema.nullable(),
    observedAt: dateTimeSchema,
});
const quantityMilliSchema = z.number().int().positive();
const moneyMinorSchema = z.number().int().nonnegative();
export const nutritionSchema = z.object({
    energyKj: z.number().nonnegative().nullable().default(null),
    energyKcal: z.number().nonnegative().nullable().default(null),
    fatG: z.number().nonnegative().nullable().default(null),
    saturatedFatG: z.number().nonnegative().nullable().default(null),
    carbohydratesG: z.number().nonnegative().nullable().default(null),
    sugarsG: z.number().nonnegative().nullable().default(null),
    fibreG: z.number().nonnegative().nullable().default(null),
    proteinG: z.number().nonnegative().nullable().default(null),
    saltG: z.number().nonnegative().nullable().default(null),
});
export const canteenProductSchema = z.object({
    id: z.string().uuid(),
    code: z.string().min(1),
    name: z.string().min(1),
    groupId: z.string().uuid().nullable(),
    groupName: z.string().nullable(),
    baseUnit: z.enum(['g', 'ml', 'piece']),
    state: z.enum(['active', 'inactive', 'withdrawn']),
    version: z.number().int().positive(),
    productVersionId: z.string().uuid(),
    allergens: z.array(z.string()),
    possibleAllergens: z.array(z.string()),
    source: z.string().min(1),
    nutritionPer100: nutritionSchema,
});
export const canteenProductGroupSchema = z.object({
    id: z.string().uuid(),
    parentId: z.string().uuid().nullable(),
    code: z.string().min(1),
    name: z.string().min(1),
    path: z.array(z.string().min(1)).min(1),
    depth: z.number().int().nonnegative(),
});
export const stockLotSchema = z.object({
    id: z.string().uuid(),
    productId: z.string().uuid(),
    productName: z.string().min(1),
    warehouseId: z.string().uuid(),
    warehouseName: z.string().min(1),
    supplier: z.string().nullable(),
    lotNumber: z.string().nullable(),
    expiryDate: z.string().date().nullable(),
    initialQuantityMilli: z.number().int().nonnegative(),
    availableQuantityMilli: z.number().int().nonnegative(),
    unitCostMinorPerBase: moneyMinorSchema,
    state: z.enum(['available', 'held', 'withdrawn', 'exhausted']),
});
export const stockMovementSchema = z.object({
    id: z.string().uuid(),
    productId: z.string().uuid(),
    productName: z.string().min(1),
    lotId: z.string().uuid(),
    lotNumber: z.string().nullable(),
    warehouseId: z.string().uuid(),
    warehouseName: z.string().min(1),
    kind: z.enum([
        'opening',
        'receipt',
        'issue',
        'return',
        'adjustment',
        'transfer',
    ]),
    direction: z.enum(['in', 'out']),
    quantityMilli: quantityMilliSchema,
    valueMinor: moneyMinorSchema,
    sourceType: z.string().min(1),
    actorId: z.string().uuid(),
    occurredAt: z.string().datetime({ offset: true }),
});
export const canteenWarehouseSchema = z.object({
    id: z.string().uuid(),
    code: z.string().min(1),
    name: z.string().min(1),
});
export const canteenRecipeLineSchema = z.object({
    id: z.string().uuid(),
    productId: z.string().uuid(),
    productName: z.string().min(1),
    grossQuantityMilli: quantityMilliSchema,
    netQuantityMilli: quantityMilliSchema,
    wasteBps: z.number().int().min(0).max(10_000),
});
export const canteenRecipeSchema = z.object({
    id: z.string().uuid(),
    code: z.string().min(1),
    name: z.string().min(1),
    groupName: z.string().min(1),
    diet: z.string().min(1),
    state: z.enum(['draft', 'published', 'withdrawn']),
    version: z.number().int().positive(),
    recipeVersionId: z.string().uuid(),
    baseServings: z.number().int().positive(),
    portionSizeMilli: quantityMilliSchema,
    portionUnit: z.string().min(1),
    instructions: z.string(),
    allergens: z.array(z.string()),
    possibleAllergens: z.array(z.string()),
    nutrition: nutritionSchema,
    nutritionPerServing: nutritionSchema,
    lines: z.array(canteenRecipeLineSchema),
});
export const productionPlanSchema = z.object({
    id: z.string().uuid(),
    serviceDate: z.string().date(),
    offerItemId: z.string().uuid(),
    offerName: z.string().min(1),
    recipeId: z.string().uuid(),
    recipeName: z.string().min(1),
    reservedServings: z.number().int().nonnegative(),
    forecastServings: z.number().int().nonnegative(),
    bufferBps: z.number().int().min(0).max(10_000),
    plannedServings: z.number().int().nonnegative(),
    status: z.enum([
        'draft',
        'calculated',
        'approved',
        'superseded',
        'cancelled',
    ]),
    version: z.number().int().positive(),
});
export const productionBatchSchema = z.object({
    id: z.string().uuid(),
    planId: z.string().uuid(),
    batchCode: z.string().min(1),
    offerName: z.string().min(1),
    status: z.enum([
        'planned',
        'preparing',
        'prepared',
        'released',
        'closed',
        'held',
        'withdrawn',
    ]),
    plannedServings: z.number().int().nonnegative(),
    actualServings: z.number().int().nonnegative().nullable(),
    varianceReason: z.string().nullable().default(null),
    /** Accepted terminal issuance less append-only reversals. */
    terminalIssuedServings: z.number().int().nonnegative().default(0),
    ingredients: z.array(z.object({
        id: z.string().uuid(),
        productName: z.string().min(1),
        baseUnit: z.string().min(1),
        lotId: z.string().uuid(),
        lotNumber: z.string().nullable(),
        quantityMilli: quantityMilliSchema,
        kind: z.enum(['used', 'substitute', 'returned', 'waste']),
        substitutionReason: z.string().nullable().default(null),
        rawMaterialReconciliation: z.object({
            state: z.enum(['pending', 'reconciled', 'legacy_assumed']),
            usedQuantityMilli: z.number().int().nonnegative(),
            returnedQuantityMilli: z.number().int().nonnegative(),
            wasteQuantityMilli: z.number().int().nonnegative(),
            wasteReason: z.string().nullable().default(null),
        }),
    })),
    outcomes: z
        .array(z.object({
        id: z.string().uuid(),
        kind: z.enum(['issued', 'reusable', 'transferred', 'disposed']),
        quantityServings: z.number().int().positive(),
        lossCategory: z
            .enum([
            'preparation_waste',
            'overproduction',
            'unissued_meals',
            'spoilage',
            'expired',
            'safety_recall',
            'operational_error',
        ])
            .nullable(),
        reason: z.string(),
        storageLocation: z.string().nullable(),
        storageConditions: z.string().nullable(),
        usableUntil: z.string().date().nullable(),
        destination: z.string().nullable(),
        recordedBy: z.string().uuid(),
        recordedAt: z.string().datetime({ offset: true }),
    }))
        .default([]),
});
export const canteenDemandLineSchema = z.object({
    id: z.string().uuid(),
    productId: z.string().uuid(),
    productName: z.string().min(1),
    requestedQuantityMilli: quantityMilliSchema,
    approvedQuantityMilli: z.number().int().nonnegative().nullable(),
    issuedQuantityMilli: z.number().int().nonnegative(),
    remainingQuantityMilli: z.number().int().nonnegative(),
    issuedValueMinor: moneyMinorSchema,
    issuedByIds: z.array(z.string().uuid()),
});
export const canteenDemandSchema = z.object({
    id: z.string().uuid(),
    number: z.string().min(1),
    warehouseId: z.string().uuid(),
    warehouseName: z.string().min(1),
    requestedOn: z.string().date(),
    purpose: z.string().min(1),
    requestedBy: z.string().uuid(),
    approvedBy: z.string().uuid().nullable(),
    status: z.enum([
        'draft',
        'submitted',
        'approved',
        'partially_issued',
        'issued',
        'rejected',
        'cancelled',
    ]),
    lines: z.array(canteenDemandLineSchema),
});
export const canteenServiceExclusionSchema = z.object({
    id: z.string().uuid(),
    serviceDate: z.string().date(),
    itemTypeId: z.string().uuid().nullable(),
    itemTypeName: z.string().min(1).nullable(),
    reason: z.string().min(1),
    createdBy: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export const foodSafetyDeviceSchema = z.object({
    id: z.string().uuid(),
    code: z.string().min(1),
    name: z.string().min(1),
    kind: z.enum(['thermometer', 'fridge', 'freezer', 'warmer', 'other']),
    state: z.enum(['active', 'blocked', 'retired']),
    calibrationValidUntil: z.string().date().nullable(),
});
export const foodSafetyCheckSchema = z.object({
    id: z.string().uuid(),
    checkType: z.string().min(1),
    subjectType: z.enum([
        'delivery',
        'stock_lot',
        'production_batch',
        'device',
        'area',
        'sample',
    ]),
    subjectId: z.string().uuid().nullable(),
    deviceId: z.string().uuid().nullable(),
    deviceName: z.string().nullable(),
    measuredValueMilli: z.number().int().nullable(),
    unit: z.string().nullable(),
    lowerLimitMilli: z.number().int().nullable(),
    upperLimitMilli: z.number().int().nullable(),
    result: z.enum(['pass', 'fail', 'late', 'missed', 'disputed']),
    source: z.enum(['manual', 'sensor', 'offline', 'system']),
    observationClassification: z.enum([
        'independent',
        'repeat',
        'verification',
        'conflict',
    ]),
    relatedCheckId: z.string().uuid().nullable(),
    disputeReason: z.string().nullable(),
    disputedBy: z.string().uuid().nullable(),
    disputedAt: z.string().datetime({ offset: true }).nullable(),
    templateVersion: z.number().int().positive(),
    scheduleId: z.string().uuid().nullable(),
    scheduledAt: z.string().datetime({ offset: true }).nullable(),
    observedAt: z.string().datetime({ offset: true }),
    notes: z.string(),
    createdBy: z.string().uuid(),
});
export const foodSafetyActionSchema = z.object({
    id: z.string().uuid(),
    checkId: z.string().uuid(),
    scheduleId: z.string().uuid().nullable(),
    description: z.string().min(1),
    state: z.enum(['open', 'in_progress', 'resolved', 'verified']),
    requiresRecheck: z.boolean(),
    resolution: z.string().nullable(),
    createdBy: z.string().uuid(),
    resolvedBy: z.string().uuid().nullable(),
    createdAt: z.string().datetime({ offset: true }),
    resolvedAt: z.string().datetime({ offset: true }).nullable(),
});
export const foodSafetyCheckTemplateSchema = z.object({
    id: z.string().uuid(),
    code: z.string().min(1),
    name: z.string().min(1),
    checkType: z.string().min(1),
    subjectType: foodSafetyCheckSchema.shape.subjectType,
    unit: z.string().nullable(),
    lowerLimitMilli: z.number().int().nullable(),
    upperLimitMilli: z.number().int().nullable(),
    templateVersion: z.number().int().positive(),
    escalationAfterMinutes: z.number().int().nonnegative(),
    defaultCorrectiveAction: z.string(),
    active: z.boolean(),
});
export const foodSafetyScheduleSchema = z.object({
    id: z.string().uuid(),
    templateId: z.string().uuid(),
    templateName: z.string().min(1),
    checkType: z.string().min(1),
    subjectType: foodSafetyCheckSchema.shape.subjectType,
    subjectId: z.string().uuid().nullable(),
    deviceId: z.string().uuid().nullable(),
    deviceName: z.string().nullable(),
    scheduledAt: z.string().datetime({ offset: true }),
    state: z.enum(['scheduled', 'missed', 'completed', 'cancelled']),
    escalationState: z.enum(['not_due', 'pending', 'escalated', 'acknowledged']),
    checkId: z.string().uuid().nullable(),
});
export const foodSafetySampleCustodyEventSchema = z.object({
    id: z.string().uuid(),
    eventType: z.enum([
        'collected',
        'transferred',
        'received',
        'compromised',
        'disposed',
    ]),
    fromActorId: z.string().uuid().nullable(),
    toActorId: z.string().uuid().nullable(),
    fromLocation: z.string().nullable(),
    toLocation: z.string().nullable(),
    storageConditions: z.string(),
    occurredAt: z.string().datetime({ offset: true }),
    notes: z.string(),
    createdBy: z.string().uuid(),
});
export const foodSafetySampleSchema = z.object({
    id: z.string().uuid(),
    productionBatchId: z.string().uuid().nullable(),
    stockLotId: z.string().uuid().nullable(),
    sampleType: z.string().min(1),
    quantityMilli: z.number().int().positive(),
    unit: z.string().min(1),
    containerCode: z.string().min(1),
    storageLocation: z.string().min(1),
    storageConditions: z.string(),
    state: z.enum(['held', 'transferred', 'disposed', 'compromised']),
    collectedBy: z.string().uuid(),
    collectedAt: z.string().datetime({ offset: true }),
    destroyAfter: z.string().datetime({ offset: true }),
    disposedAt: z.string().datetime({ offset: true }).nullable(),
    custody: z.array(foodSafetySampleCustodyEventSchema),
});
/** A versioned sanitation procedure.  A procedure describes how an area or a
 * device is made safe; its executions below remain immutable evidence. */
export const foodSafetySanitationProcedureSchema = z.object({
    id: z.string().uuid(),
    code: z.string().min(1),
    name: z.string().min(1),
    subjectType: z.enum(['area', 'device']),
    subjectName: z.string().min(1),
    deviceId: z.string().uuid().nullable(),
    method: z.string().min(1),
    agentName: z.string().min(1),
    agentVersion: z.string().min(1),
    procedureVersion: z.number().int().positive(),
    requiresVerification: z.boolean(),
    active: z.boolean(),
});
export const foodSafetySanitationRunSchema = z.object({
    id: z.string().uuid(),
    procedureId: z.string().uuid(),
    procedureName: z.string().min(1),
    procedureVersion: z.number().int().positive(),
    subjectType: z.enum(['area', 'device']),
    subjectName: z.string().min(1),
    deviceId: z.string().uuid().nullable(),
    method: z.string().min(1),
    agentName: z.string().min(1),
    agentVersion: z.string().min(1),
    requiresVerification: z.boolean(),
    scheduledAt: z.string().datetime({ offset: true }).nullable(),
    performedAt: z.string().datetime({ offset: true }).nullable(),
    performedBy: z.string().uuid().nullable(),
    verifiedAt: z.string().datetime({ offset: true }).nullable(),
    verifiedBy: z.string().uuid().nullable(),
    state: z.enum(['scheduled', 'completed', 'verified', 'missed', 'cancelled']),
    notes: z.string(),
});
export const foodRecallSchema = z.object({
    id: z.string().uuid(),
    stockLotId: z.string().uuid(),
    productName: z.string().min(1),
    lotNumber: z.string().nullable(),
    reason: z.string().min(1),
    state: z.enum(['active', 'resolved']),
    affectedBatches: z.array(z.string()),
    actualIssuedRecipientCount: z.number().int().nonnegative(),
    affectedReservationCount: z.number().int().nonnegative(),
    createdBy: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    resolution: z.string().nullable(),
});
export const supplierInvoiceLineSchema = z.object({
    id: z.string().uuid(),
    productId: z.string().uuid(),
    productName: z.string().min(1),
    quantityMilli: quantityMilliSchema,
    unitNetMinorPerBase: moneyMinorSchema,
    vatRateBps: z.number().int().min(0).max(10_000),
    netMinor: moneyMinorSchema,
    vatMinor: moneyMinorSchema,
    grossMinor: moneyMinorSchema,
    lotNumber: z.string().nullable(),
    expiryDate: z.string().date().nullable(),
    stockLotId: z.string().uuid().nullable(),
});
export const supplierInvoiceSchema = z.object({
    id: z.string().uuid(),
    supplier: z.string().min(1),
    number: z.string().min(1),
    documentDate: z.string().date(),
    receivedDate: z.string().date(),
    currency: z.literal('PLN'),
    netMinor: moneyMinorSchema,
    vatMinor: moneyMinorSchema,
    grossMinor: moneyMinorSchema,
    status: z.enum(['draft', 'approved', 'corrected', 'cancelled']),
    attachmentRef: z.string().nullable(),
    notes: z.string(),
    declaredGrossMinor: moneyMinorSchema.nullable(),
    varianceMinor: z.number().int().nullable(),
    lines: z.array(supplierInvoiceLineSchema),
});
/**
 * Actions are deliberately conservative. `require_exception` never means an
 * automatic issue: the future offline flow must require an entitled operator,
 * a reason and an audit record before it may proceed.
 */
export const offlineTerminalActionSchema = z.enum([
    'block',
    'require_exception',
]);
export const offlineTerminalPolicyInputSchema = z.object({
    packageMaxAgeMinutes: z.number().int().min(5).max(1_440),
    expiredPackageAction: offlineTerminalActionSchema,
    incompleteProfileAction: offlineTerminalActionSchema,
    staleRevocationAction: offlineTerminalActionSchema,
});
export const offlineTerminalPolicySchema = offlineTerminalPolicyInputSchema.extend({
    /** Incremented only when this policy changes, so signed terminal packages can bind to it. */
    version: z.number().int().positive(),
});
export const canteenOperationsSnapshotSchema = z.object({
    generatedAt: z.string().datetime({ offset: true }),
    warehouse: z.object({ id: z.string().uuid(), name: z.string().min(1) }),
    warehouses: z.array(canteenWarehouseSchema).min(1),
    productGroups: z.array(canteenProductGroupSchema),
    settings: z.object({
        serviceWeekdays: z.array(z.number().int().min(1).max(7)),
        reservationLeadDays: z.number().int().nonnegative(),
        cancellationLeadDays: z.number().int().nonnegative(),
        defaultCreditDays: z.number().int().nonnegative(),
        offlineTerminal: offlineTerminalPolicySchema,
    }),
    itemTypes: z.array(z.object({
        id: z.string().uuid(),
        code: z.string().min(1),
        name: z.string().min(1),
    })),
    serviceExclusions: z.array(canteenServiceExclusionSchema),
    foodSafetyDevices: z.array(foodSafetyDeviceSchema),
    foodSafetyChecks: z.array(foodSafetyCheckSchema),
    foodSafetyActions: z.array(foodSafetyActionSchema),
    foodSafetyCheckTemplates: z.array(foodSafetyCheckTemplateSchema).default([]),
    foodSafetySchedules: z.array(foodSafetyScheduleSchema).default([]),
    foodSafetySamples: z.array(foodSafetySampleSchema).default([]),
    foodSafetySanitationProcedures: z
        .array(foodSafetySanitationProcedureSchema)
        .default([]),
    foodSafetySanitationRuns: z.array(foodSafetySanitationRunSchema).default([]),
    foodRecalls: z.array(foodRecallSchema),
    /** Terminal servings intentionally recorded without a production batch. */
    unassignedTerminalIssuedServings: z.number().int().nonnegative().default(0),
    products: z.array(canteenProductSchema),
    lots: z.array(stockLotSchema),
    stockMovements: z.array(stockMovementSchema),
    recipes: z.array(canteenRecipeSchema),
    productionPlans: z.array(productionPlanSchema),
    productionBatches: z.array(productionBatchSchema),
    demands: z.array(canteenDemandSchema),
    invoices: z.array(supplierInvoiceSchema),
});
export const createCanteenProductSchema = z.object({
    code: z.string().trim().min(2).max(40),
    name: z.string().trim().min(2).max(160),
    groupId: z.string().uuid().nullable().default(null),
    baseUnit: z.enum(['g', 'ml', 'piece']),
    allergens: z.array(z.string().trim().min(1)).default([]),
    possibleAllergens: z.array(z.string().trim().min(1)).default([]),
    source: z.string().trim().min(3).max(300),
    nutritionPer100: nutritionSchema.default({
        energyKj: null,
        energyKcal: null,
        fatG: null,
        saturatedFatG: null,
        carbohydratesG: null,
        sugarsG: null,
        fibreG: null,
        proteinG: null,
        saltG: null,
    }),
});
export const createCanteenProductGroupSchema = z.object({
    code: z.string().trim().min(2).max(40),
    name: z.string().trim().min(2).max(120),
    parentId: z.string().uuid().nullable().default(null),
});
export const updateCanteenSettingsSchema = z.object({
    serviceWeekdays: z.array(z.number().int().min(1).max(7)).min(1),
    reservationLeadDays: z.number().int().min(0).max(365),
    cancellationLeadDays: z.number().int().min(0).max(365),
    defaultCreditDays: z.number().int().min(0).max(365),
    /** Omitted only by an older client; in that case the existing policy remains unchanged. */
    offlineTerminal: offlineTerminalPolicyInputSchema.optional(),
});
export const createCanteenServiceExclusionSchema = z.object({
    serviceDate: z.string().date(),
    itemTypeId: z.string().uuid().nullable().default(null),
    reason: z.string().trim().min(3).max(300),
});
export const createFoodSafetyDeviceSchema = z.object({
    code: z.string().trim().min(2).max(40),
    name: z.string().trim().min(2).max(120),
    kind: z.enum(['thermometer', 'fridge', 'freezer', 'warmer', 'other']),
    calibrationValidUntil: z.string().date().nullable().default(null),
});
export const recordFoodSafetyCheckSchema = z
    .object({
    checkType: z.string().trim().min(2).max(80),
    subjectType: z.enum([
        'delivery',
        'stock_lot',
        'production_batch',
        'device',
        'area',
        'sample',
    ]),
    subjectId: z.string().uuid().nullable().default(null),
    deviceId: z.string().uuid().nullable().default(null),
    measuredValueMilli: z.number().int().min(-100_000).max(1_000_000),
    unit: z.string().trim().min(1).max(20),
    lowerLimitMilli: z.number().int().nullable().default(null),
    upperLimitMilli: z.number().int().nullable().default(null),
    source: z.enum(['manual', 'sensor', 'offline']),
    externalObservationId: z.string().trim().max(120).nullable().default(null),
    scheduleId: z.string().uuid().nullable().default(null),
    templateVersion: z.number().int().positive().default(1),
    scheduledAt: z.string().datetime({ offset: true }).nullable().default(null),
    observedAt: z.string().datetime({ offset: true }),
    notes: z.string().trim().max(500).default(''),
    correctiveAction: z.string().trim().max(500).default(''),
})
    .refine((value) => value.lowerLimitMilli === null ||
    value.upperLimitMilli === null ||
    value.lowerLimitMilli <= value.upperLimitMilli, { path: ['upperLimitMilli'], message: 'Górny limit nie może być niższy.' })
    .superRefine((value, context) => {
    if (value.source === 'sensor' && !value.externalObservationId) {
        context.addIssue({
            code: 'custom',
            path: ['externalObservationId'],
            message: 'Odczyt czujnika musi zawierać identyfikator obserwacji.',
        });
    }
});
export const disputeFoodSafetyCheckSchema = z.object({
    reason: z.string().trim().min(5).max(500),
});
export const createFoodSafetyCheckTemplateSchema = z
    .object({
    code: z.string().trim().min(2).max(40),
    name: z.string().trim().min(2).max(160),
    checkType: z.string().trim().min(2).max(80),
    subjectType: foodSafetyCheckSchema.shape.subjectType,
    unit: z.string().trim().min(1).max(30).nullable().default(null),
    lowerLimitMilli: z.number().int().nullable().default(null),
    upperLimitMilli: z.number().int().nullable().default(null),
    escalationAfterMinutes: z.number().int().min(0).max(43_200).default(60),
    defaultCorrectiveAction: z.string().trim().max(1000).default(''),
})
    .refine((input) => input.lowerLimitMilli === null ||
    input.upperLimitMilli === null ||
    input.lowerLimitMilli <= input.upperLimitMilli, { path: ['upperLimitMilli'], message: 'Górny limit nie może być niższy.' });
export const scheduleFoodSafetyCheckSchema = z.object({
    templateId: z.string().uuid(),
    subjectId: z.string().uuid().nullable().default(null),
    deviceId: z.string().uuid().nullable().default(null),
    scheduledAt: z.string().datetime({ offset: true }),
});
export const createFoodSafetySampleSchema = z
    .object({
    productionBatchId: z.string().uuid().nullable().default(null),
    stockLotId: z.string().uuid().nullable().default(null),
    sampleType: z.string().trim().min(2).max(100),
    quantityMilli: quantityMilliSchema,
    unit: z.string().trim().min(1).max(30),
    containerCode: z.string().trim().min(2).max(100),
    storageLocation: z.string().trim().min(2).max(200),
    storageConditions: z.string().trim().max(500).default(''),
    collectedAt: z.string().datetime({ offset: true }),
    destroyAfter: z.string().datetime({ offset: true }),
    notes: z.string().trim().max(500).default(''),
})
    .refine((input) => input.productionBatchId !== null || input.stockLotId !== null, { message: 'Próbka musi wskazywać partię posiłku lub produktu.' })
    .refine((input) => input.destroyAfter > input.collectedAt, {
    path: ['destroyAfter'],
    message: 'Termin zniszczenia musi być późniejszy niż pobranie.',
});
export const transferFoodSafetySampleSchema = z.object({
    toActorId: z.string().uuid().nullable().default(null),
    toLocation: z.string().trim().min(2).max(200),
    storageConditions: z.string().trim().max(500).default(''),
    occurredAt: z.string().datetime({ offset: true }),
    notes: z.string().trim().max(500).default(''),
});
export const disposeFoodSafetySampleSchema = z.object({
    occurredAt: z.string().datetime({ offset: true }),
    reason: z.string().trim().min(3).max(500),
});
export const createFoodSafetySanitationProcedureSchema = z.object({
    code: z.string().trim().min(2).max(40),
    name: z.string().trim().min(2).max(160),
    subjectType: z.enum(['area', 'device']),
    subjectName: z.string().trim().min(2).max(160),
    deviceId: z.string().uuid().nullable().default(null),
    method: z.string().trim().min(2).max(500),
    agentName: z.string().trim().min(2).max(160),
    agentVersion: z.string().trim().min(1).max(80),
    requiresVerification: z.boolean().default(true),
});
export const scheduleFoodSafetySanitationSchema = z.object({
    procedureId: z.string().uuid(),
    scheduledAt: z.string().datetime({ offset: true }),
});
export const completeFoodSafetySanitationSchema = z.object({
    performedAt: z.string().datetime({ offset: true }),
    notes: z.string().trim().max(500).default(''),
});
export const verifyFoodSafetySanitationSchema = z.object({
    verifiedAt: z.string().datetime({ offset: true }),
    notes: z.string().trim().max(500).default(''),
});
export const resolveFoodSafetyActionSchema = z.object({
    resolution: z.string().trim().min(3).max(500),
    recheckPassed: z.boolean(),
});
export const createFoodRecallSchema = z.object({
    stockLotId: z.string().uuid(),
    reason: z.string().trim().min(3).max(500),
});
export const resolveFoodRecallSchema = z.object({
    resolution: z.string().trim().min(3).max(500),
});
export const createCanteenWarehouseSchema = z.object({
    code: z.string().trim().min(2).max(40),
    name: z.string().trim().min(2).max(120),
});
export const createStockReceiptSchema = z.object({
    productId: z.string().uuid(),
    warehouseId: z.string().uuid(),
    quantityMilli: quantityMilliSchema,
    unitCostMinorPerBase: moneyMinorSchema,
    supplier: z.string().trim().min(2).max(160),
    lotNumber: z.string().trim().max(100).nullable().default(null),
    expiryDate: z.string().date().nullable().default(null),
});
export const createStockIssueSchema = z.object({
    lotId: z.string().uuid(),
    quantityMilli: quantityMilliSchema,
    purpose: z.string().trim().min(3).max(300),
});
export const createStockAdjustmentSchema = z.object({
    lotId: z.string().uuid(),
    direction: z.enum(['in', 'out']),
    quantityMilli: quantityMilliSchema,
    reason: z.string().trim().min(3).max(300),
});
export const createStockTransferSchema = z.object({
    lotId: z.string().uuid(),
    destinationWarehouseId: z.string().uuid(),
    quantityMilli: quantityMilliSchema,
    reason: z.string().trim().min(3).max(300),
});
export const createCanteenRecipeSchema = z.object({
    code: z.string().trim().min(2).max(40),
    name: z.string().trim().min(2).max(160),
    groupName: z.string().trim().min(2).max(100),
    diet: z.string().trim().min(2).max(100),
    baseServings: z.number().int().positive(),
    portionSizeMilli: quantityMilliSchema,
    portionUnit: z.string().trim().min(1).max(20),
    instructions: z.string().trim().max(5000),
    publish: z.boolean().default(false),
    lines: z
        .array(z.object({
        productId: z.string().uuid(),
        grossQuantityMilli: quantityMilliSchema,
        netQuantityMilli: quantityMilliSchema,
    }))
        .min(1),
});
export const recipeScaleRequestSchema = z.object({
    servings: z.number().int().positive().max(100_000),
});
export const recipeScaleResultSchema = z.object({
    recipeId: z.string().uuid(),
    recipeVersionId: z.string().uuid(),
    baseServings: z.number().int().positive(),
    targetServings: z.number().int().positive(),
    allergens: z.array(z.string()),
    possibleAllergens: z.array(z.string()),
    nutrition: nutritionSchema,
    nutritionPerServing: nutritionSchema,
    lines: z.array(z.object({
        productId: z.string().uuid(),
        productName: z.string().min(1),
        grossQuantityMilli: quantityMilliSchema,
        netQuantityMilli: quantityMilliSchema,
    })),
});
export const createProductionPlanSchema = z.object({
    offerItemId: z.string().uuid(),
    recipeId: z.string().uuid(),
    forecastServings: z.number().int().nonnegative().default(0),
    bufferBps: z.number().int().min(0).max(10_000).default(0),
});
export const completeProductionBatchSchema = z
    .object({
    actualServings: z.number().int().positive(),
    varianceReason: z.string().trim().max(500).nullable().default(null),
    allocations: z
        .array(z.object({
        recipeLineId: z.string().uuid(),
        lotId: z.string().uuid(),
        quantityMilli: quantityMilliSchema,
        substitute: z.boolean().default(false),
        substitutionReason: z
            .string()
            .trim()
            .max(500)
            .nullable()
            .default(null),
    }))
        .min(1),
})
    .superRefine((input, context) => {
    input.allocations.forEach((allocation, index) => {
        if (allocation.substitute &&
            (allocation.substitutionReason?.trim().length ?? 0) < 3)
            context.addIssue({
                code: 'custom',
                path: ['allocations', index, 'substitutionReason'],
                message: 'Zamiennik wymaga krótkiego, zrozumiałego powodu.',
            });
        if (!allocation.substitute && allocation.substitutionReason)
            context.addIssue({
                code: 'custom',
                path: ['allocations', index, 'substitutionReason'],
                message: 'Powód zamiennika można podać tylko dla oznaczonego zamiennika.',
            });
    });
});
export const reconcileProductionIngredientsSchema = z
    .object({
    lines: z
        .array(z.object({
        batchIngredientId: z.string().uuid(),
        usedQuantityMilli: z.number().int().nonnegative(),
        returnedQuantityMilli: z.number().int().nonnegative(),
        wasteQuantityMilli: z.number().int().nonnegative(),
        wasteReason: z.string().trim().max(500).nullable().default(null),
    }))
        .min(1),
})
    .superRefine((input, context) => {
    const identifiers = new Set();
    input.lines.forEach((line, index) => {
        if (identifiers.has(line.batchIngredientId))
            context.addIssue({
                code: 'custom',
                path: ['lines', index, 'batchIngredientId'],
                message: 'Każdy pobrany składnik można rozliczyć tylko raz.',
            });
        identifiers.add(line.batchIngredientId);
        if (line.wasteQuantityMilli > 0 &&
            (line.wasteReason?.trim().length ?? 0) < 3)
            context.addIssue({
                code: 'custom',
                path: ['lines', index, 'wasteReason'],
                message: 'Ubytek surowca wymaga krótkiego, zrozumiałego powodu.',
            });
        if (line.wasteQuantityMilli === 0 && line.wasteReason)
            context.addIssue({
                code: 'custom',
                path: ['lines', index, 'wasteReason'],
                message: 'Powód ubytku można podać tylko dla ubytku większego od zera.',
            });
    });
});
export const reconcileProductionBatchSchema = z
    .object({
    outcomes: z
        .array(z.object({
        kind: z.enum(['issued', 'reusable', 'transferred', 'disposed']),
        quantityServings: z.number().int().positive().max(100_000),
        lossCategory: z
            .enum([
            'preparation_waste',
            'overproduction',
            'unissued_meals',
            'spoilage',
            'expired',
            'safety_recall',
            'operational_error',
        ])
            .nullable()
            .default(null),
        reason: z.string().trim().max(500).default(''),
        storageLocation: z.string().trim().max(160).nullable().default(null),
        storageConditions: z
            .string()
            .trim()
            .max(500)
            .nullable()
            .default(null),
        usableUntil: dateSchema.nullable().default(null),
        destination: z.string().trim().max(160).nullable().default(null),
    }))
        .min(1),
})
    .superRefine((input, context) => {
    for (const [index, outcome] of input.outcomes.entries()) {
        if (outcome.kind === 'disposed') {
            if (outcome.lossCategory === null)
                context.addIssue({
                    code: 'custom',
                    message: 'Utylizacja wymaga kategorii straty.',
                    path: ['outcomes', index, 'lossCategory'],
                });
            if (outcome.reason.length < 3)
                context.addIssue({
                    code: 'custom',
                    message: 'Utylizacja wymaga zrozumiałego powodu.',
                    path: ['outcomes', index, 'reason'],
                });
        }
        else if (outcome.lossCategory !== null) {
            context.addIssue({
                code: 'custom',
                message: 'Kategoria straty dotyczy wyłącznie utylizacji.',
                path: ['outcomes', index, 'lossCategory'],
            });
        }
        if (outcome.kind === 'reusable') {
            if (!outcome.storageLocation)
                context.addIssue({
                    code: 'custom',
                    message: 'Pozostałość wymaga wskazania miejsca przechowywania.',
                    path: ['outcomes', index, 'storageLocation'],
                });
            if (!outcome.storageConditions)
                context.addIssue({
                    code: 'custom',
                    message: 'Pozostałość wymaga warunków przechowywania.',
                    path: ['outcomes', index, 'storageConditions'],
                });
            if (!outcome.usableUntil)
                context.addIssue({
                    code: 'custom',
                    message: 'Pozostałość wymaga terminu bezpiecznego użycia.',
                    path: ['outcomes', index, 'usableUntil'],
                });
        }
        if (outcome.kind === 'transferred' && !outcome.destination)
            context.addIssue({
                code: 'custom',
                message: 'Przekazanie wymaga wskazania odbiorcy lub miejsca.',
                path: ['outcomes', index, 'destination'],
            });
    }
});
const demandInputLineSchema = z.object({
    productId: z.string().uuid(),
    quantityMilli: quantityMilliSchema,
});
export const createDemandSchema = z.object({
    warehouseId: z.string().uuid(),
    requestedOn: z.string().date(),
    purpose: z.string().trim().min(3).max(300),
    lines: z.array(demandInputLineSchema).min(1),
});
export const demandTransitionSchema = z.object({
    action: z.enum(['submit', 'approve', 'reject', 'cancel']),
    reason: z.string().trim().max(500).nullable().default(null),
});
export const issueDemandSchema = z.object({
    allocations: z
        .array(z.object({
        demandLineId: z.string().uuid(),
        lotId: z.string().uuid(),
        quantityMilli: quantityMilliSchema,
    }))
        .min(1),
});
export const createSupplierInvoiceSchema = z.object({
    warehouseId: z.string().uuid(),
    supplier: z.string().trim().min(2).max(160),
    number: z.string().trim().min(1).max(100),
    documentDate: z.string().date(),
    receivedDate: z.string().date(),
    attachmentRef: z.string().trim().max(500).nullable().default(null),
    notes: z.string().trim().max(1000).default(''),
    declaredGrossMinor: moneyMinorSchema.nullable().default(null),
    approve: z.boolean().default(false),
    lines: z
        .array(z.object({
        productId: z.string().uuid(),
        quantityMilli: quantityMilliSchema,
        unitNetMinorPerBase: moneyMinorSchema,
        vatRateBps: z.number().int().min(0).max(10_000),
        lotNumber: z.string().trim().max(100).nullable().default(null),
        expiryDate: z.string().date().nullable().default(null),
    }))
        .min(1),
});
